import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import {
  Alert,
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import {useFormik} from 'formik';
import {
  forwardRef,
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useGetAssetHumanType} from '../../hooks/get-commtrac-node-type';
import {useAppSelector} from '../../hooks/redux';
import {
  AssetHuman,
  AssetHumanListQuery,
  AssetHumanListResponse,
} from '../../interfaces/AssetHumanAdmin';
import reduxSelectors from '../../redux/selectors';
import {getHumanReadable} from '../../utils/macAddress';
import AccessControl from '../common/AccessControl';
import DataGrid, {DataGridColumn, DataGridRef} from '../common/DataGrid';
import EmployeeAssetItemsPurgeButton from '../employees/buttons/EmployeeAssetItemsPurgeButton';
import AssetHumanItemEditButton from './AssetHumanItemEditButton';

interface Props {}

export interface AssetHumanListRef {
  fetch?: () => void;
  dataGridRef: MutableRefObject<DataGridRef | null>;
}

const AssetHumanList = forwardRef<AssetHumanListRef, Props>((__, ref) => {
  /*******/
  /* ref */
  /*******/
  useImperativeHandle(ref, () => ({
    fetch: () => fetchData(formik.values),
    dataGridRef,
  }));

  const isDarkMode = useAppSelector(reduxSelectors.app.getIsDarkMode);
  const paperBg = isDarkMode ? '#222222' : '#FFF';
  const zones = useAppSelector(({assets}) => assets.zones);
  const shifts = useAppSelector(({assets}) => assets.shifts);
  const productsEnabled = useAppSelector(reduxSelectors.assets.productsEnabled);
  const assetHumanTypes = useAppSelector(
    ({assets}) => assets.asset_human_types
  );
  const minerAddressMask = useAppSelector(
    ({assets}) => assets.constants?.miner.address_mask
  );

  const statusOptions: {
    value: AssetHumanListQuery['status'];
    label: string;
  }[] = [
    {value: 'all', label: 'All'},
    {value: 'active', label: 'Active'},
    {value: 'inactive', label: 'Inactive'},
  ];

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<AssetHumanListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const getAssetHumanType = useGetAssetHumanType();

  const fetchData = async (params: AssetHumanListQuery) => {
    setFetchedInProgress(true);
    try {
      const resp = await API.get<AssetHumanListResponse>(
        `${apiBaseUrl}/asset-human`,
        {params}
      );
      setFetchedData(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_904338ef');
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  const onRefresh = () => {
    fetchData(formik.values);
  };
  /*************/
  /* data grid */
  /*************/
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedIds?.includes(i.id)),
    [rows, selectedIds]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const toggleSelectItem = (id: number) => {
    if (selectedIds?.includes(id)) {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectAll = () => {
    setSelectedIds(rows?.map((i) => i.id) ?? []);
  };

  const unselectAll = () => {
    setSelectedIds([]);
  };

  const toggleSelectAllItems = () => {
    if (selectedIds.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const columns: DataGridColumn<AssetHuman>[] = [
    {
      field: 'select',
      type: 'select',
      doNotExport: true,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedIds.length > 0 && selectedAll}
          indeterminate={selectedIds.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row}) => (
        <Box>
          <Checkbox
            color="primary"
            checked={selectedIds.includes(row.id)}
            onChange={() => toggleSelectItem(row.id)}
          />
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      sortable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 60,
      headerName: 'Edit',
      sxHeader: {textAlign: 'center'},
      sxCell: {textAlign: 'center'},
      renderCell: ({row}) => {
        return (
          <Box display="flex" gap={1} justifyContent="end">
            <AccessControl permissions={['patch::/asset-human/:id']}>
              <AssetHumanItemEditButton
                pk={row.id}
                item={row}
                isAdminSection={true}
                prefetch
                component={IconButton}
                componentProps={{color: 'primary', size: 'small'}}
                onSubmitted={() => fetchData(formik.values)}
                mode="update"
              >
                <MoreHorizIcon />
              </AssetHumanItemEditButton>
            </AccessControl>
          </Box>
        );
      },
    },
    {
      field: 'mc2_flag',
      headerName: 'MC2',
      width: 60,
      hidden: !productsEnabled.includes('connect'),
      valueGetter: ({row}) =>
        row.commtrac_node ? (row.commtrac_node?.mc2_flag ? 'Yes' : 'No') : '',
    },
    {
      field: 'personal_id',
      headerName: 'Employee ID',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'commtrac_external_id',
      headerName: 'Network ID',
      width: 120,
      resizeable: true,
      sortable: true,
      hidden: !productsEnabled.includes('connect'),
      valueGetter: ({row}) => {
        if (row.commtrac_external_id && minerAddressMask) {
          // eslint-disable-next-line no-bitwise
          return row.commtrac_external_id & minerAddressMask;
        }
      },
    },
    {
      field: 'mac_address',
      headerName: 'MAC-address',
      width: 200,
      resizeable: true,
      sortable: true,
      hidden: !productsEnabled.includes('connect'),
      valueGetter: ({row}) => {
        if (row.mac_address) {
          return getHumanReadable(row.mac_address);
        }
      },
    },
    {
      field: 'external_id',
      headerName: 'Proximity ID',
      width: 120,
      resizeable: true,
      sortable: true,
      hidden: !productsEnabled.includes('proximity'),
    },
    {
      field: 'nickname',
      headerName: 'Nickname',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'first_name',
      headerName: 'First Name',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'birth_date',
      headerName: 'Birthday',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
      resizeable: true,
      sortable: false,
      valueGetter: ({row}) => {
        if (row.commtrac_node) {
          return getAssetHumanType({
            commtrac_external_id: row.commtrac_node?.commtrac_external_id,
            wifi_enabled: row.commtrac_node?.wifi_enabled,
            mc2_flag: row.commtrac_node?.mc2_flag,
          });
        } else {
          return null;
        }
      },
    },
    {
      field: 'type_id',
      headerName: 'Job Type',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) =>
        assetHumanTypes.find((i) => i.id === row.type_id)?.name,
    },
    {
      field: 'zone_id',
      headerName: 'Section',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => zones.find((i) => i.id === row.zone_id)?.name,
    },
    {
      field: 'shift_id',
      headerName: 'Shift',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => shifts.find((i) => i.id === row.shift_id)?.name,
    },
    {
      field: 'status',
      sortable: true,
      width: 80,
      resizeable: true,
      renderCell: ({row}) => {
        return row.status === 'active' ? (
          <ToggleOnIcon color="success" />
        ) : (
          <ToggleOffIcon color="error" />
        );
      },
    },
    {
      field: 'commtrac_report_frequency',
      headerName: 'Reporting Frequency',
      width: 190,
      resizeable: true,
      hidden: !productsEnabled.includes('connect'),
      valueGetter: ({row}) => row.commtrac_node?.commtrac_report_frequency,
    },
    {
      field: 'cam_monitor_battery',
      headerName: 'Monitor Battery',
      width: 140,
      resizeable: true,
      hidden: !productsEnabled.includes('connect'),
      valueGetter: ({row}) => {
        if (row.commtrac_node) {
          return getAssetHumanType({
            commtrac_external_id: row.commtrac_node?.cam_monitor_battery,
            wifi_enabled: row.commtrac_node?.wifi_enabled,
            mc2_flag: row.commtrac_node?.mc2_flag,
          }) === 'CAM' && !row.commtrac_node?.cam_monitor_battery
            ? 'No'
            : 'Yes';
        } else {
          return null;
        }
      },
    },
    {
      field: 'has_motion_sensor',
      headerName: 'Motion Sensor',
      width: 140,
      resizeable: true,
      hidden: !productsEnabled.includes('connect'),
      valueGetter: ({row}) =>
        row.commtrac_node?.has_motion_sensor
          ? row.commtrac_node?.has_motion_sensor
            ? 'Yes'
            : 'No'
          : '',
    },
    {
      field: 'date_activated',
      headerName: 'Activated Timestamp',
      width: 180,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'date_deactivated',
      headerName: 'Deactivated Timestamp',
      width: 180,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'last_update',
      headerName: 'Last Seen',
      width: 140,
      resizeable: true,
      sortable: true,
      hidden: !productsEnabled.includes('connect'),
    },
  ];

  const formik = useFormik<AssetHumanListQuery>({
    initialValues: {
      page: 0,
      limit: 25,
      order: 'id',
      dir: 'ASC',
      status: 'all',
      filter: null,
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    fetchData(formik.values);
  }, [formik.values]);

  return (
    <Paper
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        p: 3,
        bgcolor: paperBg,
        backgroundImage: 'none',
      }}
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}
      <Box display="flex" flexDirection="column" height="100%">
        <Box pb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="left"
            height={40}
            width={200}
            gap={2}
          >
            <Box flexGrow={1} display="flex" width="200px" height="100%">
              <TextField
                fullWidth
                label="Status"
                select
                SelectProps={{
                  multiple: false,
                }}
                value={formik.values.status}
                onChange={(el) => {
                  formik.setFieldValue('status', el.target.value);

                  if (el.target.value === 'all') {
                    formik.setFieldValue('filter', null);
                  } else {
                    formik.setFieldValue(
                      'filter',
                      JSON.stringify({
                        type: 'and',
                        filter: [
                          {
                            field: 'status',
                            op: 'in',
                            val: [el.target.value],
                          },
                        ],
                      })
                    );
                  }
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value ?? ''} value={option.value ?? ''}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>
        <DataGrid
          ref={dataGridRef}
          rows={rows}
          columns={columns}
          loading={fetchedInProgress}
          pagination
          paginationMode="server"
          size="small"
          sx={{
            backgroundColor: 'unset',
            th: {
              bgcolor: paperBg,
            },
          }}
          sortBy={{
            field: formik.values.order,
            dir: formik.values.dir === 'DESC' ? 'desc' : 'asc',
          }}
          sortingMode="server"
          page={formik.values.page}
          pageSize={formik.values.limit}
          rowCount={fetchedData?.count}
          onPageChange={(v) => formik.setFieldValue('page', v)}
          onPageSizeChange={(v) => {
            formik.setFieldValue('limit', v);
            formik.setFieldValue('page', 0);
          }}
          onSort={(v) => {
            if (v) {
              formik.setFieldValue('order', v.field);
              formik.setFieldValue('dir', v.dir.toUpperCase());
            }
          }}
          footerStart={
            <Box display="flex" alignItems="center" gap={3} px={1}>
              <Box
                display="flex"
                gap={0.5}
                alignItems="center"
                height="100%"
                whiteSpace="nowrap"
              >
                {selectedRows.length} selected
              </Box>
              <AccessControl
                permissions={['post::/purge']}
                amountAffected={selectedRows.length}
              >
                <Box display="flex" height={40}>
                  <EmployeeAssetItemsPurgeButton
                    employeeIds={selectedRows.map((i) => i.id)}
                    componentProps={{size: 'small', variant: 'outlined'}}
                    onDone={onRefresh}
                    disabled={selectedRows.length === 0}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" sx={{mr: 1}} />{' '}
                    Purge
                  </EmployeeAssetItemsPurgeButton>
                </Box>
              </AccessControl>
            </Box>
          }
        />
      </Box>
    </Paper>
  );
});

export default AssetHumanList;
